import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';

export default function SellerAnalyticsDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, profileRes] = await Promise.all([
          API.get(`/profile/${user.id}/stats`),
          API.get(`/profile/${user.id}/profile`)
        ]);
        setStats(statsRes.data.stats);
        setProfile(profileRes.data.user);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) fetchData();
  }, [user?.id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: 20, marginBottom: 8 }} className="float-anim">📊</div>
        <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>Loading your stats...</div>
      </div>
    );
  }

  if (!stats || !profile) return null;

  const badgeDescriptions = {
    trusted: { label: '🏆 Trusted Seller', desc: '5+ reviews, 4.5+ stars, ID verified' },
    quick: { label: '⚡ Quick Responder', desc: 'Reply in < 2 hours average' },
  };

  const userBadges = profile.badges || [];
  const activeBadges = userBadges.filter(b => b.type !== 'email' && b.type !== 'phone' && b.type !== 'id');
  const verificationBadges = userBadges.filter(b => ['email', 'phone', 'id'].includes(b.type));

  const TIERS = [
    { label: 'Newcomer',     icon: '🆕', min: 0,   max: 5,   color: '#90A4AE', bg: '#ECEFF1' },
    { label: 'Rising Seller',icon: '🌟', min: 5,   max: 25,  color: '#1976D2', bg: '#E3F2FD' },
    { label: 'Top Seller',   icon: '⭐', min: 25,  max: 100, color: '#F57C00', bg: '#FFF3E0' },
    { label: 'Power Seller', icon: '🏆', min: 100, max: null, color: '#C0392B', bg: '#FDECEA' },
  ];
  const totalSales = parseInt(stats.total_sales || 0) + parseInt(profile.total_rentals || 0);
  const currentTier = TIERS.slice().reverse().find(t => totalSales >= t.min) || TIERS[0];
  const nextTier = TIERS[TIERS.indexOf(currentTier) + 1] || null;
  const tierProgress = nextTier ? Math.min(100, Math.round(((totalSales - currentTier.min) / (nextTier.min - currentTier.min)) * 100)) : 100;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, marginBottom: 24 }}>
          📊 Your Performance
        </h2>

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 32 }}>
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1976D2', marginBottom: 8 }}>
              {profile.rating_avg ? parseFloat(profile.rating_avg).toFixed(1) : '—'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 600 }}>Average Rating</div>
            <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 4 }}>
              ({profile.rating_count} reviews)
            </div>
          </div>

          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#2E7D32', marginBottom: 8 }}>
              {profile.avg_response_hours ? Math.round(parseFloat(profile.avg_response_hours)) : '—'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 600 }}>Avg Response Time</div>
            <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 4 }}>hours</div>
          </div>

          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#FF9800', marginBottom: 8 }}>
              {stats.active_listings || 0}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 600 }}>Active Listings</div>
          </div>

          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#C0392B', marginBottom: 8 }}>
              {stats.total_sales || 0}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 600 }}>Total Sales</div>
          </div>
        </div>

        {/* Seller Tier */}
        <div style={{ background: currentTier.bg, border: `1px solid ${currentTier.color}40`, borderRadius: 14, padding: 20, marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: currentTier.color }}>
            {currentTier.icon} Seller Tier: {currentTier.label}
          </h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-soft)', marginBottom: 6 }}>
            <span>{totalSales} total transaction{totalSales !== 1 ? 's' : ''}</span>
            {nextTier && <span>{nextTier.min - totalSales} more to reach {nextTier.label} {nextTier.icon}</span>}
            {!nextTier && <span style={{ color: currentTier.color, fontWeight: 700 }}>Max tier reached!</span>}
          </div>
          <div style={{ background: 'rgba(0,0,0,0.1)', borderRadius: 100, height: 8, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 100, background: currentTier.color, width: `${tierProgress}%`, transition: 'width 0.6s ease' }} />
          </div>
          {nextTier && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {TIERS.map((t, i) => (
                <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700, background: totalSales >= t.min ? t.bg : '#F5F5F5', color: totalSales >= t.min ? t.color : '#BBB', border: `1px solid ${totalSales >= t.min ? t.color + '60' : '#E0E0E0'}` }}>
                  {t.icon} {t.label}
                  <span style={{ fontWeight: 400, opacity: 0.7 }}>({t.min}+)</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Badges Section */}
        {verificationBadges.length > 0 || activeBadges.length > 0 ? (
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
            <h3 style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Your Badges</h3>

            {verificationBadges.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 }}>
                  Verifications
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {verificationBadges.map(badge => (
                    <div
                      key={badge.type}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 12px',
                        background: '#E3F2FD',
                        border: '1px solid #64B5F6',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#0D47A1'
                      }}
                    >
                      {badge.icon} {badge.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeBadges.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 }}>
                  Earned Badges
                </div>
                <div style={{ display: 'grid', gap: 12 }}>
                  {activeBadges.map(badge => (
                    <div
                      key={badge.type}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 12,
                        background: badge.type === 'trusted' ? '#FFF3E0' : '#E8F5E9',
                        border: badge.type === 'trusted' ? '1px solid #FFB74D' : '1px solid #81C784',
                        borderRadius: 10
                      }}
                    >
                      <span style={{ fontSize: 20 }}>{badge.icon}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{badgeDescriptions[badge.type]?.label || badge.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                          {badgeDescriptions[badge.type]?.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeBadges.length === 0 && (
              <div style={{ padding: '20px', background: 'var(--cream)', borderRadius: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 14, color: 'var(--text-mid)', marginBottom: 8 }}>
                  Keep building your reputation!
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                  Earn badges by getting great reviews and replying quickly to messages
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Revenue Section */}
      {stats.revenue !== undefined && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
          <h3 style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>💰 Revenue</h3>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--cherry)', marginBottom: 8 }}>
            ${parseFloat(stats.revenue || 0).toFixed(2)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
            from {stats.total_sales || 0} completed transactions
          </div>
        </div>
      )}
    </div>
  );
}
